import { useEffect, useState } from "react";
import { api, EvidenceBadge, PILLARS, FORMATS } from "@/lib/api";
import { toast } from "sonner";
import { Wand2, RefreshCcw, CheckCircle, Trash2 } from "lucide-react";

export default function MonthlyPlan() {
  const [plans, setPlans] = useState([]);
  const [active, setActive] = useState(null);
  const [mode, setMode] = useState("balanced");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/monthly_plans").then((r) => { setPlans(r.data); if (r.data[0] && !active) setActive(r.data[0]); });
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/generate_monthly_plan", { mode });
      toast.success(`Plan v${data.version} generated with ${data.entries.length} posts`);
      setActive(data);
      load();
    } catch (e) { toast.error("Plan generation failed"); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    const { data } = await api.post(`/monthly_plans/${active.id}/commit`);
    toast.success(`${data.created} entries added to calendar`);
    load();
  };

  const removePlan = async () => {
    if (!confirm(`Delete plan ${active.month} v${active.version}?`)) return;
    await api.delete(`/monthly_plans/${active.id}`);
    toast.success("Deleted");
    setActive(null);
    load();
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Monthly Plan</div>
          <h1 className="text-5xl font-black tracking-tight leading-none" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Next month's plan</h1>
        </div>
        <div className="flex gap-2">
          <select data-testid="mode-select" value={mode} onChange={(e) => setMode(e.target.value)} className="border border-neutral-300 rounded-md px-3 text-sm">
            <option value="conservative">Conservative</option>
            <option value="balanced">Balanced (70/20/10)</option>
            <option value="growth">Growth (50/30/20)</option>
          </select>
          <button data-testid="generate-plan-btn" onClick={generate} disabled={busy} className="px-4 py-2.5 rounded-md font-semibold text-sm text-neutral-900 flex items-center gap-2 disabled:opacity-50" style={{ background: "#D4FF00" }}>
            {busy ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Generate plan
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        <div className="bg-white border border-neutral-200 rounded-lg p-4 h-fit">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Versions</div>
          <div className="space-y-1">
            {plans.map((p) => (
              <button key={p.id} onClick={() => setActive(p)} data-testid={`plan-v-${p.id}`}
                className={`w-full text-left p-2 rounded transition-colors text-sm ${active?.id === p.id ? "bg-neutral-100 font-semibold" : "hover:bg-neutral-50"}`}>
                {p.month} · v{p.version}
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{p.mode} · {p.approval_status}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          {active ? (
            <>
              <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{active.month} · v{active.version}</h2>
                    <div className="text-xs text-neutral-500 mt-1">{active.entries.length} posts · Mode: {active.mode}</div>
                  </div>
                  {active.approval_status !== "approved" && (
                    <button data-testid="commit-plan-btn" onClick={commit} className="px-3 py-2 rounded text-xs font-semibold text-neutral-900 flex items-center gap-1" style={{ background: "#D4FF00" }}>
                      <CheckCircle className="w-3 h-3" />Approve + commit to calendar
                    </button>
                  )}
                  <button data-testid="delete-plan-btn" onClick={removePlan} className="ml-2 px-3 py-2 rounded text-xs font-semibold border border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-1"><Trash2 className="w-3 h-3" />Delete</button>
                </div>
                {active.rationale && <p className="text-sm text-neutral-600 mt-3 leading-relaxed">{active.rationale}</p>}
              </div>

              <div className="space-y-3">
                {active.entries.map((e, i) => (
                  <div key={i} className="bg-white border border-neutral-200 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{PILLARS[e.pillar] || e.pillar}</span>
                          <span className="text-[10px] text-neutral-400">·</span>
                          <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{FORMATS[e.format] || e.format}</span>
                          <EvidenceBadge confidence={e.confidence} />
                        </div>
                        <div className="font-bold text-lg leading-snug" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{e.working_title}</div>
                        <div className="text-sm text-neutral-700 mt-1">Hook: <span className="italic">{e.recommended_hook}</span></div>
                        <div className="text-xs text-neutral-500 mt-2">{e.why_selected}</div>
                        {e.supporting_data && <div className="text-xs text-neutral-500 mt-1"><span className="uppercase font-semibold tracking-widest">Evidence:</span> {e.supporting_data}</div>}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-black" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{e.publish_date?.split("-")[2]}</div>
                        <div className="text-[10px] text-neutral-500 uppercase tracking-widest">{e.publish_date?.slice(0, 7)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="bg-white border border-dashed border-neutral-300 rounded-lg p-16 text-center text-neutral-500 text-sm">No plan yet. Click Generate plan.</div>
          )}
        </div>
      </div>
    </div>
  );
}
